import { SessionStatus } from '@sunrise-ui/api/events/api';
import { useMemo } from 'react';
import {
  useGetBulkSessionData,
  useGetSessionsByEventId,
} from '../queries/sessions';
import { SessionRole } from '../utils/sessions';

export type UseIsEventSpeakerOrModeratorOptions = {
  enabled?: boolean;
};

export type UseIsEventSpeakerOrModeratorResult = {
  isAllowed: boolean;
  isLoading: boolean;
};

/**
 * Determines if a given user is a speaker or moderator for any SCHEDULED session in an event.
 * Keeps UI thin by encapsulating the role-check logic and data-fetching here.
 */
export const useIsEventSpeakerOrModerator = (
  eventId?: string,
  userId?: string,
  options?: UseIsEventSpeakerOrModeratorOptions,
): UseIsEventSpeakerOrModeratorResult => {
  const { enabled = true } = options ?? {};

  // Always fetch sessions by event to know which ones are scheduled
  const { data: allSessions, isLoading: isLoadingSessions } =
    useGetSessionsByEventId(eventId, false);

  // Only consider scheduled or confirmed sessions for gating this page
  const scheduledOrConfirmedSessions = useMemo(
    () =>
      allSessions?.filter(
        (s) =>
          s.status === SessionStatus.Scheduled ||
          (s.status as unknown as string) === 'confirmed',
      ) ?? [],
    [allSessions],
  );

  const sessionIds = useMemo(
    () =>
      scheduledOrConfirmedSessions
        .map((s) => s.id)
        .filter((id): id is string => Boolean(id)),
    [scheduledOrConfirmedSessions],
  );

  // Fetch bulk attendees for the scheduled sessions only
  const { data: bulkSessionData, isLoading: isLoadingBulk } =
    useGetBulkSessionData({
      sessionIds,
    });

  const isAllowed = useMemo(() => {
    if (!userId) return false;
    const data = bulkSessionData?.session_data;
    if (!data || data.length === 0) return false;

    // Check if user is speaker or moderator in any scheduled session
    for (const session of data) {
      const attendees = session.data?.attendees ?? [];
      const match = attendees.some(
        ({ user_id, role }) =>
          user_id === userId &&
          (role === SessionRole.SPEAKER || role === SessionRole.MODERATOR),
      );
      if (match) return true;
    }
    return false;
  }, [bulkSessionData?.session_data, userId]);

  const isLoading =
    Boolean(enabled) &&
    (isLoadingSessions || (sessionIds.length > 0 && isLoadingBulk));

  return { isAllowed, isLoading };
};
